import { useEffect, useRef, useState } from "react";
import { animate, motion } from "framer-motion";
import { ArrowRight, TrendingUp } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { trackEvent } from "../lib/tracking";
import Reveal from "./Reveal";

const fmt = (n) => "$" + Math.round(n).toLocaleString("en-AU");
const FEE_PER_YEAR = 3000; // $250/month membership

export const Calculator = () => {
  const [gci, setGci] = useState(200000);
  const [split, setSplit] = useState(75);
  const [hqSplit, setHqSplit] = useState(85);
  const [years, setYears] = useState(1);
  const [displayExtra, setDisplayExtra] = useState(0);
  const trackedRef = useRef(null);

  const keepNow = (gci * split) / 100;
  const keepHq = (gci * hqSplit) / 100 - FEE_PER_YEAR;
  const extraPerYear = Math.max(keepHq - keepNow, 0);
  const extraTotal = extraPerYear * years;

  // animate the headline number whenever it changes
  useEffect(() => {
    const controls = animate(displayExtra, extraTotal, {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplayExtra(v),
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraTotal]);

  // track calculator usage (max once per 1.2s of inactivity)
  useEffect(() => {
    if (trackedRef.current) clearTimeout(trackedRef.current);
    trackedRef.current = setTimeout(() => {
      trackEvent("calculator_use", { gci, current_split: split, agency_hq_split: hqSplit, extra_per_year: Math.round(extraPerYear) });
    }, 1200);
    return () => clearTimeout(trackedRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gci, split, hqSplit]);

  const discuss = () => {
    const msg = `Hi — I used your earnings calculator. I currently generate about ${fmt(gci)} in commission a year and keep ${split}% of it. On ${hqSplit}% retention through Agency HQ I'd be roughly ${fmt(extraPerYear)} better off per year. I'd like to discuss my numbers confidentially.`;
    window.dispatchEvent(new CustomEvent("ahq-prefill", { detail: msg }));
    trackEvent("cta_click", { location: "calculator", target: "contact" });
    scrollToId("contact");
  };

  const fill = (v, min, max) => ({ "--fill": `${((v - min) / (max - min)) * 100}%` });

  return (
    <section id="calculator" className="border-y border-white/10 bg-surface/50 py-16 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <p className="label-mono">Earnings calculator</p>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="calculator-heading">
            See what staying where you are could be costing you.
          </h2>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-2 lg:gap-10">
          {/* inputs */}
          <Reveal delay={0.05}>
            <div className="rounded-2xl border border-white/10 bg-panel/60 p-6 sm:p-8">
              <div className="flex items-baseline justify-between">
                <label htmlFor="gci-range" className="text-sm font-medium text-slate-300">
                  Your annual commission (GCI)
                </label>
                <output
                  htmlFor="gci-range"
                  className="font-display text-2xl font-extrabold text-white"
                  data-testid="calculator-gci-input-display"
                >
                  {fmt(gci)}
                </output>
              </div>
              <input
                id="gci-range"
                type="range"
                min="100000"
                max="2000000"
                step="10000"
                value={gci}
                onChange={(e) => setGci(+e.target.value)}
                className="ahq-range mt-4"
                style={fill(gci, 100000, 2000000)}
                data-testid="calculator-gci-slider"
              />
              <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-widest text-slate-600">
                <span>$100k</span>
                <span>$2M</span>
              </div>

              <div className="mt-9">
                <p className="text-sm font-medium text-slate-300">Your current commission split</p>
                <div className="mt-3 grid grid-cols-3 gap-2" role="group" aria-label="Current commission split">
                  {[70, 75, 80].map((s) => (
                    <button
                      key={s}
                      onClick={() => setSplit(s)}
                      data-testid={`calculator-split-option-${s}`}
                      aria-pressed={split === s}
                      className={`rounded-xl border py-3 font-display text-lg font-bold transition-all duration-200 ${
                        split === s
                          ? "border-gold bg-gold/15 text-gold-light"
                          : "border-white/10 text-slate-400 hover:border-white/25 hover:text-white"
                      }`}
                    >
                      {s}%
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-9">
                <div className="flex items-baseline justify-between">
                  <label htmlFor="hq-range" className="text-sm font-medium text-slate-300">
                    Retained with Agency HQ
                  </label>
                  <span className="font-display text-2xl font-extrabold text-gold-light" data-testid="calculator-hq-split-display">
                    {hqSplit}%
                  </span>
                </div>
                <input
                  id="hq-range"
                  type="range"
                  min="80"
                  max="95"
                  step="1"
                  value={hqSplit}
                  onChange={(e) => setHqSplit(+e.target.value)}
                  className="ahq-range mt-4"
                  style={fill(hqSplit, 80, 95)}
                  data-testid="calculator-hq-split-slider"
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-widest text-slate-600">
                  <span>80%</span>
                  <span>up to 95%</span>
                </div>
              </div>

              <p className="mt-8 rounded-xl border border-white/10 bg-ink/50 p-4 text-xs leading-relaxed text-slate-500">
                Assumes the Agency HQ monthly administration membership from $250 (−$3,000 p.a.).
                Indicative only — your exact split is confirmed confidentially before you join.
              </p>
            </div>
          </Reveal>

          {/* results */}
          <Reveal delay={0.12}>
            <div className="flex h-full flex-col rounded-2xl border border-gold/40 bg-gradient-to-b from-gold/[0.08] to-transparent p-6 sm:p-8">
              <div className="flex items-center gap-2.5 text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
                <TrendingUp className="h-4 w-4 text-gold-light" />
                Your numbers
              </div>

              <div className="mt-7 space-y-4">
                <div className="flex items-baseline justify-between border-b border-white/5 pb-4">
                  <span className="text-sm text-slate-400">What you keep today</span>
                  <span className="font-display text-xl font-bold text-slate-300">{fmt(keepNow)}</span>
                </div>
                <div className="flex items-baseline justify-between border-b border-white/5 pb-4">
                  <span className="text-sm text-slate-400">With Agency HQ, after fees</span>
                  <span className="font-display text-xl font-bold text-white">{fmt(keepHq)}</span>
                </div>
              </div>

              <div className="mt-8">
                <p className="text-sm text-slate-400">
                  Staying put could be costing you, per year:
                </p>
                <p
                  className="mt-2 font-display text-5xl font-extrabold tracking-tight text-gold-light sm:text-6xl"
                  data-testid="calculator-result-extra-amount"
                >
                  {fmt(displayExtra)}
                </p>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-2" role="group" aria-label="Projection period">
                {[1, 3, 5].map((y) => (
                  <button
                    key={y}
                    onClick={() => setYears(y)}
                    data-testid={`calculator-years-option-${y}`}
                    aria-pressed={years === y}
                    className={`rounded-lg border px-2 py-2.5 text-xs font-semibold transition-all duration-200 ${
                      years === y
                        ? "border-gold bg-gold/15 text-gold-light"
                        : "border-white/10 text-slate-400 hover:border-white/25 hover:text-white"
                    }`}
                  >
                    {y} yr{y > 1 ? "s" : ""} · {fmt(extraPerYear * y)}
                  </button>
                ))}
              </div>

              <p className="mt-6 flex-1 text-sm leading-relaxed text-slate-400">
                That is <strong className="text-white">{fmt(extraTotal)}</strong> over {years === 1 ? "one year" : `${years} years`} —
                money currently going to fees instead of your account.
              </p>

              <button
                onClick={discuss}
                data-testid="calculator-cta-discuss-btn"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-7 py-4 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-500/25 transition-all duration-200 hover:from-amber-600 hover:to-amber-700 active:scale-[0.98]"
              >
                Discuss my numbers confidentially
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
